import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import { api } from '@/lib/api/client'

/** The report's path segment under /v1/reports. */
export type ReportKey = 'ptp' | 'visits' | 'attendance'

/** TEXT | INTEGER | AMOUNT | DISTANCE | DATE | TIME | COORDINATES | LINK */
export interface GridColumn {
  header: string
  type: string
}

/** One page of a report. Cells arrive formatted as the file shows them. */
export interface GridResponse {
  columns: GridColumn[]
  rows: string[][]
  total: number
  page: number
  size: number
}

/** What the filters may offer this user — already narrowed to their scope. */
export interface ReportOptions {
  maxRangeDays: number
  /** Today in the lender's zone (YYYY-MM-DD): the latest To allowed. */
  today: string
  nodes: { id: string; name: string; type: string }[]
  people: { id: string; name: string; empId: string }[]
  designations: { code: string; label: string }[]
}

export type ReportParams = Record<string, string | string[] | undefined>

export interface GridQuery {
  search: string
  sort: number | null
  desc: boolean
  page: number
  size: number
}

/**
 * Spring binds a repeated `status=A&status=B`; axios's default
 * `status[]=A` would arrive as nothing at all.
 */
const paramsSerializer = { indexes: null }

export function useReportOptions(key: ReportKey) {
  return useQuery({
    queryKey: ['report-options', key],
    queryFn: async () => (await api.get<ReportOptions>(`/v1/reports/${key}/options`)).data,
    staleTime: 5 * 60_000,
  })
}

/** Runs only once the filters have been applied (`params` non-null). */
export function useReportPage(key: ReportKey, params: ReportParams | null, grid: GridQuery) {
  return useQuery({
    queryKey: ['report', key, params, grid],
    enabled: params !== null,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (
        await api.get<GridResponse>(`/v1/reports/${key}`, {
          params: {
            ...params,
            ...(grid.search.trim() ? { search: grid.search.trim() } : {}),
            ...(grid.sort !== null ? { sort: grid.sort, desc: grid.desc } : {}),
            page: grid.page,
            size: grid.size,
          },
          paramsSerializer,
        })
      ).data,
  })
}

/**
 * Downloads through axios rather than an `<a href>`: the endpoint needs the
 * bearer token. The file is named as the server names it, from
 * Content-Disposition.
 */
export async function downloadReport(key: ReportKey, params: ReportParams, format: 'xlsx' | 'csv') {
  const res = await api.get<Blob>(`/v1/reports/${key}.${format}`, {
    params,
    paramsSerializer,
    responseType: 'blob',
  })
  const url = URL.createObjectURL(res.data)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = fileNameFrom(res.headers['content-disposition']) ?? `${key}-report.${format}`
    a.click()
  } finally {
    URL.revokeObjectURL(url)
  }
}

export function fileNameFrom(disposition: string | undefined): string | null {
  if (!disposition) return null
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
  if (star?.[1]) return decodeURIComponent(star[1])
  const plain = /filename="?([^";]+)"?/i.exec(disposition)
  return plain?.[1] ?? null
}

/**
 * The server's own message for a refused report — a bad range, too many
 * rows. A blob download carries its JSON error body as a Blob, so that is
 * read out rather than lost.
 */
export async function reportError(err: unknown): Promise<string> {
  if (isAxiosError(err)) {
    let body: unknown = err.response?.data
    if (body instanceof Blob) {
      try {
        body = JSON.parse(await body.text())
      } catch {
        body = null
      }
    }
    const message = (body as { message?: string } | null)?.message
    if (message) return message
    if (err.response?.status === 403) return 'You do not have access to this report.'
    return err.message
  }
  return String(err)
}

/** The visit's proof photo as an object URL; the caller revokes it. */
export async function fetchVisitPhoto(visitId: string): Promise<string> {
  const res = await api.get<Blob>(`/v1/reports/visits/${visitId}/photo`, { responseType: 'blob' })
  return URL.createObjectURL(res.data)
}

const DAY_MS = 86_400_000

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/**
 * The range a report opens with: the last week. For a date that can lie
 * ahead (a promise's), the last week and the weeks after it, filling the
 * cap — a promise taken today is due later, and a range ending today would
 * hide it.
 */
export function defaultRange(today: string, maxDays: number, futureAllowed: boolean): { from: string; to: string } {
  const back = Math.min(futureAllowed ? 7 : 6, maxDays - 1)
  const from = addDays(today, -back)
  return { from, to: futureAllowed ? addDays(from, maxDays - 1) : today }
}

/**
 * The server's range rule, checked before asking it: both dates, From ≤ To,
 * To ≤ today (unless the date can lie ahead, like a promise's), at most
 * `maxDays` days counting both ends. Messages match the server's word for
 * word, so the person reads the same thing either way.
 */
export function rangeError(
  from: string,
  to: string,
  today: string,
  maxDays: number,
  futureAllowed = false,
): string | null {
  if (!from || !to) return 'Choose both a From and a To date.'
  if (from > to) return 'From must be on or before To.'
  if (!futureAllowed && to > today) return 'To cannot be later than today.'
  const days = Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1
  if (days > maxDays) return `Choose a range of at most ${maxDays} days; this one is ${days} days.`
  return null
}
